import { FieldItem } from "@smart/bridge-intake-components-dom";
import { roleProviderId } from "@smart/bridge-smokeball-basic";

export type ContactData = Record<string, any>;

type ContactTypeConfig = {
  prefix: string;
  searchFieldName: string;
  formatDisplayName: (data: ContactData) => string;
};

type ContactType = "person" | "company";

const contactTypeConfigs: Record<ContactType, ContactTypeConfig> = {
  person: {
    prefix: "person/",
    searchFieldName: "person/firstName",
    formatDisplayName: (data) => {
      const parts = [
        data["person/firstName"],
        data["person/middleName"],
        data["person/lastName"],
      ].filter(Boolean);
      return parts.join(" ");
    },
  },
  company: {
    prefix: "company/",
    searchFieldName: "company/name",
    formatDisplayName: (data) => data["company/name"] || "",
  },
};

export const getContactFieldKey = (
  fieldName: string,
  contactType: ContactType,
): string | null => {
  const config = contactTypeConfigs[contactType];
  return config && fieldName.startsWith(config.prefix) ? fieldName : null;
};

export const findRelatedContactFields = (
  currentField: FieldItem,
  allFields: FieldItem[],
  contactType: ContactType,
): FieldItem[] => {
  if (!currentField.layout?.id) {
    return [];
  }

  const config = contactTypeConfigs[contactType];
  if (!config || !currentField.field?.name?.startsWith(config.prefix)) {
    return [];
  }

  const roleId = currentField.layout.id;
  const { providerId } = currentField.layout;
  const { parentId } = currentField.layout;
  const isLayoutContact =
    currentField.groupUri && providerId && providerId !== roleProviderId;

  return allFields.filter(
    (f) =>
      f.layout?.id === roleId &&
      f.layout?.providerId === providerId &&
      f.layout?.parentId === parentId &&
      f.field?.name?.startsWith(config.prefix) &&
      (isLayoutContact ? f.groupUri === currentField.groupUri : true),
  );
};

export const extractContactDataFromResponses = (
  fields: FieldItem[],
  responses: Record<string, any>,
  contactType: ContactType,
  groupIndex?: number,
): ContactData | null => {
  const contactData = fields.reduce((acc, field) => {
    const fieldKey = getContactFieldKey(field.field?.name || "", contactType);
    if (!fieldKey) return acc;

    const isGroupField =
      field.groupUri &&
      groupIndex !== undefined &&
      Array.isArray(responses[field.uri]);
    const value = isGroupField
      ? responses[field.uri][groupIndex]
      : responses[field.uri];

    if (value === null || value === undefined) return acc;

    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed ? { ...acc, [fieldKey]: trimmed } : acc;
    }

    return { ...acc, [fieldKey]: value };
  }, {} as ContactData);

  return Object.keys(contactData).length > 0 ? contactData : null;
};

export const findMatchingContacts = ({
  searchValue,
  fields,
  responses,
  currentField,
  contactType,
}: {
  searchValue: string;
  fields: FieldItem[];
  responses: Record<string, any>;
  currentField: FieldItem;
  contactType: ContactType;
}): ContactData[] => {
  if (!searchValue || searchValue.trim().length < 2) {
    return [];
  }

  if (!currentField.layout) {
    return [];
  }

  const config = contactTypeConfigs[contactType];
  if (!config) return [];

  const searchLower = searchValue.toLowerCase().trim();

  const searchFields = fields.filter(
    (f) =>
      f.layout?.id &&
      f.field?.name === config.searchFieldName &&
      f.uri !== currentField.uri,
  );

  return searchFields.flatMap((searchField) => {
    const fieldValue = responses[searchField.uri];
    if (!fieldValue) return [];

    if (searchField.groupUri && Array.isArray(fieldValue)) {
      return fieldValue
        .map((value, i) => {
          if (
            typeof value === "string" &&
            value.toLowerCase().includes(searchLower)
          ) {
            const relatedFields = findRelatedContactFields(
              searchField,
              fields,
              contactType,
            );
            return extractContactDataFromResponses(
              relatedFields,
              responses,
              contactType,
              i,
            );
          }

          return null;
        })
        .filter((data): data is ContactData => data !== null);
    }

    if (
      typeof fieldValue === "string" &&
      fieldValue.toLowerCase().includes(searchLower)
    ) {
      const relatedFields = findRelatedContactFields(
        searchField,
        fields,
        contactType,
      );
      const contactData = extractContactDataFromResponses(
        relatedFields,
        responses,
        contactType,
      );
      return contactData ? [contactData] : [];
    }

    return [];
  });
};

export const formatContactDisplayName = (
  contact: ContactData,
  contactType: ContactType,
): string => {
  const config = contactTypeConfigs[contactType];
  return config.formatDisplayName(contact);
};
